import React, { FC } from 'react';
import { ActionButtonsProps, PhotoAdjustDialogLayoutProps, TitleProps } from './PhotoAdjustDialogLayout.types';
import { ResponsiveDialog } from '../../../ResponsiveDialog';
import { closeButtonOverrides, drawerOverrides } from '../../../ResponsiveDialog/ResponsiveDialog.styles';
import { pxToRem, styled } from '@withjoy/joykit/theme';
import { Box, ButtonV2, CloseButton, Flex, TextV2 } from '@withjoy/joykit';
import { buttonSharedStates } from '@shared/utils/style/buttons';
import { useTranslation } from '@shared/core';

const StyledActionButton = styled(ButtonV2)`
  ${buttonSharedStates};
`;

export const PhotoAdjustDialogLayout: FC<PhotoAdjustDialogLayoutProps> & {
  Title: FC<TitleProps>;
  ActionButtons: FC<ActionButtonsProps>;
} = props => {
  const { isOpen, onClose, children } = props;

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      hideDivider={true}
      removeBodyPadding={true}
      hasCloseButton={false}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingY: 8 } },
      }}
      dialogOverrides={{
        Content: { props: { width: ['100%', '480px'] } },
        Body: { props: { paddingX: 8, paddingY: 8 } },
      }}
      disableFocusLock={true}
      disableCloseOnOutsideClick={true}
      disableCloseOnEscapeKeyClick={true}
    >
      {children}
    </ResponsiveDialog>
  );
};

const Title: FC<TitleProps> = props => {
  const { title, onClose } = props;
  const { tq } = useTranslation('joykit');

  return (
    <Box
      paddingX={7}
      paddingBottom={5}
      position="relative"
    >
      <Flex
        alignItems="center"
        height={8}
      >
        <TextV2
          as="h3"
          width="100%"
          typographyVariant={['hed3', 'hed6']}
          textAlign="center"
          color="mono14"
        >
          {title}
        </TextV2>
      </Flex>
      <CloseButton
        overrides={{
          Root: { props: { backgroundColor: 'white', ...closeButtonOverrides.Root?.props, position: 'absolute', top: pxToRem(-8), right: pxToRem(-8), zIndex: 'docked' } },
          Icon: { props: { ...closeButtonOverrides.Icon?.props } },
        }}
        onClick={onClose}
        aria-label={tq`Close`}
      />
    </Box>
  );
};

const ActionButtons: FC<ActionButtonsProps> = props => {
  const { handleSave, onClose } = props;

  const { t } = useTranslation('joykit');
  const photoAdjustDialogTranslationFn = t('photoAdjustDialog');

  return (
    <Flex
      flexDirection="column"
      width="100%"
      gap={5}
      marginTop={pxToRem(56)}
    >
      <StyledActionButton
        intent="neutral"
        onClick={handleSave}
      >
        {photoAdjustDialogTranslationFn.save()}
      </StyledActionButton>
      <StyledActionButton
        color="mono14"
        variant="ghost"
        backgroundColor="transparent"
        onClick={onClose}
      >
        {photoAdjustDialogTranslationFn.cancel()}
      </StyledActionButton>
    </Flex>
  );
};

PhotoAdjustDialogLayout.Title = Title;
PhotoAdjustDialogLayout.ActionButtons = ActionButtons;
PhotoAdjustDialogLayout.displayName = 'PhotoAdjustDialogLayout';
